import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { Spinner } from '@shared/components';
import type { Popup, PopupCountByStatus, PopupStatus } from '../../models/Popup';
import { CreatePopupButton, FiltersSection, StatusTabs } from '../interaction';
import { PopupsEmptyState } from './PopupsEmptyState';
import { PopupsList } from './PopupsList';

interface PopupsViewProps {
  popups: Popup[];
  totalCount: number;
  hasAnyPopups: boolean;
  statusTab: PopupStatus;
  statusCounts: PopupCountByStatus;
  isLoadingCounts?: boolean;
  searchTerm: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isFiltering?: boolean;
  onStatusTabChange: (tab: PopupStatus) => void;
  onSearchChange: (value: string) => void;
  onRefetch: () => void;
  onPreview: (popup: Popup) => void;
  onViewEvents: (popup: Popup) => void;
  onArchive: (popup: Popup) => void;
  onRestore: (popup: Popup) => void;
  onDelete: (popup: Popup) => void;
}

function PopupsListLoading() {
  const { t } = useTranslation('popups');

  return (
    <div
      className="flex min-h-[12rem] flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-border bg-background"
      data-testid="popups-list-loading"
    >
      <Spinner size="md" />
      <p className="text-sm text-muted-foreground">{t('loading.list')}</p>
    </div>
  );
}

export function PopupsView({
  popups,
  totalCount,
  hasAnyPopups,
  statusTab,
  statusCounts,
  isLoadingCounts = false,
  searchTerm,
  isLoading = false,
  isFetching = false,
  isFiltering = false,
  onStatusTabChange,
  onSearchChange,
  onRefetch,
  onPreview,
  onViewEvents,
  onArchive,
  onRestore,
  onDelete,
}: PopupsViewProps) {
  const { t } = useTranslation('popups');
  const [searchInitialValue] = useState(() => searchTerm);

  const hasFilteredResults = popups.length > 0;
  const isSearchWithNoResults = hasAnyPopups && !hasFilteredResults && searchTerm.trim().length > 0;
  const showingCount = popups.length;
  const isFiltered = searchTerm.trim().length > 0;
  const isListLoading = isLoading || isFetching || isFiltering;

  return (
    <div className={`${configurationPageClass} pb-6`} data-testid="popups-view">
      <ConfigurationPageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        action={hasAnyPopups ? <CreatePopupButton onPopupCreated={onRefetch} /> : undefined}
      />

      <div className={`${configurationPageContentClass} flex min-h-0 flex-col overflow-hidden`}>
        {hasAnyPopups && (
          <>
            <div>
              <StatusTabs
                activeTab={statusTab}
                onTabChange={onStatusTabChange}
                statusCounts={statusCounts}
                isLoadingCounts={isLoadingCounts}
              />
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <FiltersSection initialValue={searchInitialValue} onSearchChange={onSearchChange} />
              <p className="shrink-0 text-xs text-muted-foreground sm:text-sm">
                {isFiltered
                  ? t('list.countFiltered', { count: showingCount, total: totalCount })
                  : t('list.count', { count: totalCount })}
              </p>
            </div>
          </>
        )}

        <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden">
          {isListLoading ? (
            <PopupsListLoading />
          ) : !hasAnyPopups ? (
            <PopupsEmptyState onPopupCreated={onRefetch} />
          ) : isSearchWithNoResults ? (
            <div
              className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
              data-testid="popups-no-results"
            >
              <p className="text-sm font-medium text-foreground">{t('emptyState.noResultsTitle')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('emptyState.noResults')}</p>
            </div>
          ) : !hasFilteredResults ? (
            <div
              className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
              data-testid="popups-empty-tab"
            >
              <p className="text-sm font-medium text-foreground">{t('emptyState.tabEmptyTitle')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('emptyState.tabEmptyDescription')}</p>
            </div>
          ) : (
            <PopupsList
              popups={popups}
              onPreview={onPreview}
              onViewEvents={onViewEvents}
              onArchive={onArchive}
              onRestore={onRestore}
              onDelete={onDelete}
            />
          )}
        </div>
      </div>
    </div>
  );
}
